/**
 * One shared PumpPortal WebSocket (docs: do not open many connections).
 * subscribeTokenTrade with { keys: [mint] } on one connection.
 *
 * Keys: see `pumpPortalConfig.ts` — `VITE_PUMPPORTAL_API_KEY` and/or in-app storage.
 * @see https://pumpportal.fun/data-api/real-time/
 */

import {
  getEffectivePumpPortalApiKey,
  isPumpPortalPublicOnly,
  isPumpPortalWsDebugEnabled,
} from "@/lib/pumpPortalConfig";

const PUMP_WS = "wss://pumpportal.fun/api/data";

function buildWsUrl(): string {
  const key = getEffectivePumpPortalApiKey();
  const publicOnly = isPumpPortalPublicOnly();
  if (key === "backend-configured") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    // Check if laserstream is enabled locally (admin setting)
    const useLaserstream = typeof localStorage !== 'undefined' && localStorage.getItem('USE_LASERSTREAM') === 'true';
    if (useLaserstream) {
      return `${protocol}//${host}/api/laserstream`;
    }
    return `${protocol}//${host}/api/pumpportal-ws`;
  }
  if (!publicOnly && key) {
    const u = new URL(PUMP_WS);
    u.searchParams.set("api-key", key);
    return u.toString();
  }
  return PUMP_WS;
}

export type PumpPortalTradeHandler = (msg: Record<string, unknown>) => void;

const handlersByMint = new Map<string, Set<PumpPortalTradeHandler>>();
const handlersByAccount = new Map<string, Set<PumpPortalTradeHandler>>();
const newTokenListeners = new Set<PumpPortalTradeHandler>();
const allTradesListeners = new Set<PumpPortalTradeHandler>();
const connListeners = new Set<(open: boolean) => void>();
const serverErrorListeners = new Set<(msg: string) => void>();

function emitConnection(open: boolean) {
  for (const cb of connListeners) cb(open);
}

function emitServerError(msg: string) {
  for (const cb of serverErrorListeners) {
    try {
      cb(msg);
    } catch {
      /* ignore */
    }
  }
}

export function subscribePumpPortalServerErrors(
  cb: (msg: string) => void,
): () => void {
  serverErrorListeners.add(cb);
  return () => serverErrorListeners.delete(cb);
}

// ── Metrics Tracking ──
const statsListeners = new Set<(stats: { totalMessages: number; failedCalls: number; startTime: number }) => void>();
let totalMessages = 0;
let failedCalls = 0;
let streamStartTime = Date.now();

function emitStats() {
  for (const cb of statsListeners) {
    try {
      cb({ totalMessages, failedCalls, startTime: streamStartTime });
    } catch { /* ignore */ }
  }
}

export function subscribePumpPortalStats(
  cb: (stats: { totalMessages: number; failedCalls: number; startTime: number }) => void,
): () => void {
  statsListeners.add(cb);
  cb({ totalMessages, failedCalls, startTime: streamStartTime });
  return () => statsListeners.delete(cb);
}

let ws: WebSocket | null = null;
let disconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let lastMessageAt = 0;
let healthTimer: ReturnType<typeof setInterval> | null = null;
const HEALTH_CHECK_INTERVAL_MS = 10_000;
const SILENCE_THRESHOLD_MS = 45_000;

function mintsWithListeners(): string[] {
  return [...handlersByMint.entries()]
    .filter(([, set]) => set.size > 0)
    .map(([m]) => m);
}

function accountsWithListeners(): string[] {
  return [...handlersByAccount.entries()]
    .filter(([, set]) => set.size > 0)
    .map(([a]) => a);
}

function hasAnyListeners(): boolean {
  return newTokenListeners.size > 0 || allTradesListeners.size > 0 || mintsWithListeners().length > 0 || accountsWithListeners().length > 0;
}

function sendWhenOpen(payload: unknown) {
  if (ws?.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }
}

function cancelDelayedDisconnect() {
  if (disconnectTimer) {
    clearTimeout(disconnectTimer);
    disconnectTimer = null;
  }
}

function scheduleSocketClose() {
  cancelDelayedDisconnect();
  disconnectTimer = setTimeout(() => {
    disconnectTimer = null;
    if (hasAnyListeners()) return;
    try {
      ws?.close();
    } catch {
      /* ignore */
    }
    ws = null;
  }, 600);
}

function tradeMintFromPayload(msg: Record<string, unknown>): string | null {
  // Top-level mint variations
  const m = msg.mint || msg.coin || msg.tokenMint || msg.token_mint || msg.tokenAddress || msg.token_address || msg.baseMint || msg.base_mint;
  if (typeof m === "string" && m.trim()) return m.trim();

  // Nested in common wrapper keys
  const params = msg.params;
  if (params != null && typeof params === "object") {
    if (Array.isArray(params)) {
      for (const el of params) {
        if (el && typeof el === "object" && !Array.isArray(el)) {
          const sub = tradeMintFromPayload(el as Record<string, unknown>);
          if (sub) return sub;
        }
      }
    } else {
      const sub = tradeMintFromPayload(params as Record<string, unknown>);
      if (sub) return sub;
    }
  }

  const jr = msg.result;
  if (jr != null && typeof jr === "object" && !Array.isArray(jr)) {
    const sub = tradeMintFromPayload(jr as Record<string, unknown>);
    if (sub) return sub;
  }

  const inner = msg.data ?? msg.payload ?? msg.trade ?? msg.event;
  if (inner && typeof inner === "object" && !Array.isArray(inner)) {
    const o = inner as Record<string, unknown>;
    const im = o.mint ?? o.coin ?? o.tokenMint ?? o.token_mint ?? o.baseMint ?? o.base_mint ?? o.address;
    if (typeof im === "string" && im.trim()) return im.trim();
  }
  
  if (Array.isArray(inner)) {
    for (const el of inner) {
      if (!el || typeof el !== "object" || Array.isArray(el)) continue;
      const sub = tradeMintFromPayload(el as Record<string, unknown>);
      if (sub) return sub;
    }
  }
  return null;
}

function dispatchTradePayload(msg: Record<string, unknown>) {
  if (typeof msg.message === "string" && Object.keys(msg).length === 1) return;
  
  totalMessages++;
  if (totalMessages % 10 === 0) {
    emitStats(); // emit periodically to avoid React spam
  }
  
  // Global trade listeners
  for (const fn of allTradesListeners) {
    try { fn(msg); } catch { /* ignore */ }
  }

  const mintKey = tradeMintFromPayload(msg);
  if (!mintKey) return;

  // Route creation events (new token launches) to nursery/new-token subscribers
  if (newTokenListeners.size > 0) {
    const txType = typeof msg.txType === "string" ? msg.txType.toLowerCase() : "";
    const isCreate = txType === "create" ||
      (typeof msg.name === "string" && msg.name.length > 0 &&
       typeof msg.symbol === "string" && msg.symbol.length > 0 &&
       (typeof msg.bondingCurveKey === "string" || typeof msg.bonding_curve === "string"));
    if (isCreate) {
      for (const fn of newTokenListeners) {
        try { fn(msg); } catch { /* ignore */ }
      }
    }
  }

  const subs = handlersByMint.get(mintKey);
  if (subs?.size) {
    for (const fn of subs) {
      try { fn(msg); } catch { /* ignore */ }
    }
  }

  // Account trade listeners
  const accountKey = typeof msg.traderPublicKey === "string" ? msg.traderPublicKey : 
                     (typeof msg.owner === "string" ? msg.owner : null);
  if (accountKey) {
    const accSubs = handlersByAccount.get(accountKey);
    if (accSubs?.size) {
      for (const fn of accSubs) {
        try { fn(msg); } catch { /* ignore */ }
      }
    }
  }
}

function maybeEmitServerError(obj: Record<string, unknown>) {
  const e = obj.errors;
  if (typeof e === "string" && e.trim()) emitServerError(e.trim());

  const er = obj.error;
  if (typeof er === "string" && er.trim()) emitServerError(er.trim());
  if (er && typeof er === "object" && !Array.isArray(er)) {
    const om = (er as Record<string, unknown>).message;
    if (typeof om === "string" && om.trim()) emitServerError(om.trim());
  }
}

/** True if this object itself looks like a trade row (vs a wrapper frame). */
function looksLikeTradeFrame(msg: Record<string, unknown>): boolean {
  if (typeof msg.mint === "string" && msg.mint.trim()) return true;
  if (typeof msg.signature === "string" || typeof msg.sig === "string" || typeof msg.tx_hash === "string") return true;
  if (typeof msg.isBuy === "boolean" || typeof msg.is_buy === "boolean") return true;
  if (typeof msg.isBuy === "number" && Number.isFinite(msg.isBuy)) return true;
  if (typeof msg.is_buy === "number" && Number.isFinite(msg.is_buy)) return true;
  if (typeof msg.txType === "string" && msg.txType.trim()) return true;
  if (typeof msg.side === "string" && msg.side.trim()) return true;
  if (typeof msg.action === "string" && msg.action.trim()) return true;
  return false;
}

/** PumpPortal sometimes wraps one or many trades in arrays under varying keys. */
const TRADE_BATCH_KEYS = [
  "data",
  "payload",
  "trades",
  "events",
  "messages",
  "updates",
  "results",
  "items",
  "result",
  "trade",
  "event"
] as const;

function dispatchIncomingParsed(parsed: unknown) {
  if (parsed == null) return;
  if (Array.isArray(parsed)) {
    for (const item of parsed) dispatchIncomingParsed(item);
    return;
  }
  if (typeof parsed !== "object") return;
  const o = parsed as Record<string, unknown>;
  maybeEmitServerError(o);

  // If this is a batch, recurse into its items
  for (const k of TRADE_BATCH_KEYS) {
    const v = o[k];
    if (!Array.isArray(v) || v.length === 0) continue;
    const allObjects = v.every((x) => x != null && typeof x === "object" && !Array.isArray(x));
    if (!allObjects) continue;
    for (const item of v) dispatchIncomingParsed(item);
    return;
  }

  // If not a trade frame itself, drill into common nesting keys
  if (!looksLikeTradeFrame(o)) {
    const nest = o.data ?? o.payload ?? o.result ?? o.params ?? o.trade ?? o.event;
    if (nest != null && typeof nest === "object") {
      dispatchIncomingParsed(nest);
      return;
    }
  }

  dispatchTradePayload(o);
}

function startHealthCheck() {
  if (healthTimer) return;
  healthTimer = setInterval(() => {
    if (!hasAnyListeners()) return;
    const state = ws?.readyState;
    if (state == null || state === WebSocket.CLOSED) {
      ws = null;
      ensureSocket();
      return;
    }
    if (state !== WebSocket.OPEN) return;
    if (lastMessageAt > 0 && Date.now() - lastMessageAt > SILENCE_THRESHOLD_MS) {
      try { ws?.close(); } catch { /* ignore */ }
    }
  }, HEALTH_CHECK_INTERVAL_MS);
}

function stopHealthCheck() {
  if (healthTimer) {
    clearInterval(healthTimer);
    healthTimer = null;
  }
}

function attachHandlers(socket: WebSocket) {
  socket.onopen = () => {
    reconnectAttempt = 0;
    lastMessageAt = Date.now();
    const keys = mintsWithListeners();
    if (keys.length) {
      console.log(`[PumpPortal WS] Subscribing to ${keys.length} mints:`, keys);
      sendWhenOpen({ method: "subscribeTokenTrade", keys });
    }
    const accounts = accountsWithListeners();
    if (accounts.length) {
      console.log(`[PumpPortal WS] Subscribing to ${accounts.length} accounts:`, accounts);
      sendWhenOpen({ method: "subscribeAccountTrade", keys: accounts });
    }
    if (newTokenListeners.size > 0) sendWhenOpen({ method: "subscribeNewToken" });
    if (allTradesListeners.size > 0) sendWhenOpen({ method: "subscribeAllTokenTrades" });
    emitConnection(true);
    startHealthCheck();
  };

  socket.onmessage = (ev) => {
    lastMessageAt = Date.now();
    let parsed: unknown;
    try {
      parsed = JSON.parse(String(ev.data));
    } catch {
      return;
    }
    if (isPumpPortalWsDebugEnabled()) {
      try {
        const s = JSON.stringify(parsed);
        console.warn("[PumpPortal WS RECV]", s.length > 1200 ? `${s.slice(0, 1200)}…` : s);
      } catch {
        console.warn("[PumpPortal WS RECV]", parsed);
      }
    }
    dispatchIncomingParsed(parsed);
  };

  socket.onclose = () => {
    if (ws === socket) ws = null;
    emitConnection(false);
    if (!hasAnyListeners()) {
      stopHealthCheck();
      return;
    }
    const delay = Math.min(1_200 * 2 ** reconnectAttempt, 15_000);
    reconnectAttempt += 1;
    setTimeout(() => ensureSocket(), delay);
  };
}

function ensureSocket() {
  cancelDelayedDisconnect();
  if (!hasAnyListeners()) {
    stopHealthCheck();
    return;
  }
  if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) return;
  ws = new WebSocket(buildWsUrl());
  attachHandlers(ws);
}

export function subscribeNewTokens(handler: PumpPortalTradeHandler): () => void {
  newTokenListeners.add(handler);
  cancelDelayedDisconnect();
  ensureSocket();
  if (ws?.readyState === WebSocket.OPEN) sendWhenOpen({ method: "subscribeNewToken" });
  return () => {
    newTokenListeners.delete(handler);
    if (!hasAnyListeners()) scheduleSocketClose();
  };
}

export function refreshPumpPortalSocket(): void {
  cancelDelayedDisconnect();
  reconnectAttempt = 0;
  lastMessageAt = 0;
  const active = hasAnyListeners();
  try {
    ws?.close();
  } catch {
    /* ignore */
  }
  ws = null;
  emitConnection(false);
  if (active) ensureSocket();
}

export function subscribePumpPortalSocketState(
  cb: (open: boolean) => void,
): () => void {
  connListeners.add(cb);
  cb(ws?.readyState === WebSocket.OPEN);
  return () => connListeners.delete(cb);
}

export function subscribePumpPortalTokenTrades(
  mint: string,
  handler: PumpPortalTradeHandler,
): () => void {
  const key = mint.trim();
  if (!key) return () => {};

  cancelDelayedDisconnect();

  const set = handlersByMint.get(key) ?? new Set<PumpPortalTradeHandler>();
  const firstListenerForMint = set.size === 0;
  set.add(handler);
  handlersByMint.set(key, set);

  ensureSocket();
  if (ws?.readyState === WebSocket.OPEN && firstListenerForMint) {
    console.log(`[PumpPortal WS] Dynamic subscription for mint: ${key}`);
    sendWhenOpen({ method: "subscribeTokenTrade", keys: [key] });
  }

  return () => {
    const s = handlersByMint.get(key);
    if (s) {
      s.delete(handler);
      if (s.size === 0) {
        handlersByMint.delete(key);
        if (ws?.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ method: "unsubscribeTokenTrade", keys: [key] }));
          } catch { /* ignore */ }
        }
      }
    }
    if (mintsWithListeners().length === 0 && accountsWithListeners().length === 0) scheduleSocketClose();
  };
}

export function subscribePumpPortalAccountTrades(
  account: string,
  handler: PumpPortalTradeHandler,
): () => void {
  const key = account.trim();
  if (!key) return () => {};

  cancelDelayedDisconnect();

  const set = handlersByAccount.get(key) ?? new Set<PumpPortalTradeHandler>();
  const firstListenerForAccount = set.size === 0;
  set.add(handler);
  handlersByAccount.set(key, set);

  ensureSocket();
  if (ws?.readyState === WebSocket.OPEN && firstListenerForAccount) {
    console.log(`[PumpPortal WS] Dynamic subscription for account: ${key}`);
    sendWhenOpen({ method: "subscribeAccountTrade", keys: [key] });
  }

  return () => {
    const s = handlersByAccount.get(key);
    if (s) {
      s.delete(handler);
      if (s.size === 0) {
        handlersByAccount.delete(key);
        if (ws?.readyState === WebSocket.OPEN) {
          try {
            ws.send(JSON.stringify({ method: "unsubscribeAccountTrade", keys: [key] }));
          } catch { /* ignore */ }
        }
      }
    }
    if (mintsWithListeners().length === 0 && accountsWithListeners().length === 0) scheduleSocketClose();
  };
}

export function subscribeAllTokenTrades(handler: PumpPortalTradeHandler): () => void {
  allTradesListeners.add(handler);
  cancelDelayedDisconnect();
  ensureSocket();
  if (ws?.readyState === WebSocket.OPEN) sendWhenOpen({ method: "subscribeAllTokenTrades" });
  return () => {
    allTradesListeners.delete(handler);
    if (!hasAnyListeners()) scheduleSocketClose();
  };
}
